import { useState } from "react";
import { motion } from "framer-motion";
import { Lock, ArrowRight, Loader2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { EASE } from "@/components/Motion";

const CsbGate = ({ onUnlock }) => {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [shake, setShake] = useState(0);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/csb/access", { password });
      sessionStorage.setItem("csb_token", data.token);
      onUnlock(data.token);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
      setShake((s) => s + 1);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-midnight flex flex-col items-center justify-center px-6 relative overflow-hidden" data-testid="csb-gate">
      <div className="absolute -top-40 -left-40 w-[32rem] h-[32rem] rounded-full bg-brand-blue/15 blur-[150px]" />
      <div className="absolute -bottom-40 -right-40 w-[32rem] h-[32rem] rounded-full bg-brand-purple/15 blur-[150px]" />

      <motion.p
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: EASE }}
        className="font-sora font-extrabold text-xl tracking-tight mb-16"
        data-testid="csb-gate-logo"
      >
        Zilleneal<span className="text-gradient">®</span>
      </motion.p>

      <motion.div
        key={shake}
        initial={{ opacity: 0, y: 28 }}
        animate={{ opacity: 1, y: 0, x: shake ? [0, -10, 10, -6, 6, 0] : 0 }}
        transition={{ duration: 0.8, ease: EASE }}
        className="relative w-full max-w-lg rounded-3xl border border-white/10 bg-surface/90 backdrop-blur-xl p-10 md:p-14 text-center"
      >
        <span className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-1.5 text-[11px] uppercase tracking-[0.25em] text-white/50">
          <Lock className="w-3.5 h-3.5 text-brand-cyan" /> Private Website Concept
        </span>
        <h1 className="font-sora font-extrabold tracking-tighter text-4xl md:text-5xl mt-8 leading-[1.05]" data-testid="csb-gate-title">
          Chai Sutta Bar <span className="text-gradient">Canada</span>
        </h1>
        <p className="text-sm text-white/50 mt-5 leading-relaxed">
          An independent website redesign concept prepared by Zilleneal Digital.
        </p>

        <form onSubmit={submit} className="mt-10 space-y-4" data-testid="csb-gate-form">
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter access password"
            required
            autoFocus
            data-testid="csb-password-input"
            className="w-full rounded-xl border border-white/15 bg-white/5 px-5 py-4 text-sm text-center tracking-widest text-white placeholder:text-white/30 outline-none transition-colors duration-300 focus:border-brand-cyan/70"
          />
          {error && (
            <p className="text-sm text-red-400" data-testid="csb-gate-error">{error}</p>
          )}
          <button
            type="submit"
            disabled={loading}
            data-testid="csb-enter-button"
            className="group w-full inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-brand-blue to-brand-purple px-8 py-4 font-semibold transition-transform duration-300 hover:scale-[1.02] disabled:opacity-60"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                Enter Presentation
                <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
              </>
            )}
          </button>
        </form>

        <p className="text-[11px] text-white/30 mt-8">
          Confidential — prepared for the Chai Sutta Bar Canada team.
        </p>
      </motion.div>
    </div>
  );
};

export default CsbGate;
